import { useState } from "react";
import { X, Trash2, Code2, Copy, Check } from "lucide-react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export const ConnectionsModal = ({ isOpen, onClose, database, onConnectionKilled }) => {
  const [killDialogOpen, setKillDialogOpen] = useState(false);
  const [selectedPid, setSelectedPid] = useState(null);
  const [killing, setKilling] = useState(false);
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [copied, setCopied] = useState(false);

  const formatDuration = (duration) => {
    if (!duration) return "0s";
    const parts = [];
    if (duration.days) parts.push(`${duration.days}d`);
    if (duration.hours) parts.push(`${duration.hours}h`);
    if (duration.minutes) parts.push(`${duration.minutes}m`);
    if (duration.seconds) parts.push(`${duration.seconds}s`);
    return parts.join(" ") || "0s";
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "--";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStateBadge = (state) => {
    const stateMap = {
      active: { variant: "success", label: "Active" },
      idle: { variant: "secondary", label: "Idle" },
      waiting: { variant: "warning", label: "Waiting" },
    };
    const stateInfo = stateMap[state?.toLowerCase()] || { variant: "secondary", label: state };
    
    return (
      <Badge
        variant="outline"
        className={`font-mono ${
          stateInfo.variant === "success"
            ? "bg-success/10 text-success border-success/30"
            : stateInfo.variant === "warning"
            ? "bg-warning/10 text-warning border-warning/30"
            : "bg-secondary"
        }`}
      >
        {stateInfo.label}
      </Badge>
    );
  };

  const handleKillConnection = async (pid) => {
    setKilling(true);
    try {
      const response = await axios.get(`https://coopsys.app/metrics/pg/kill/${pid}`);
      toast.success(`Process ${pid} terminated successfully`, {
        description: "The connection has been killed."
      });
      setKillDialogOpen(false);
      setSelectedPid(null);
      
      // Notify parent to refresh data
      if (onConnectionKilled) {
        onConnectionKilled();
      }
    } catch (error) {
      console.error("Error killing connection:", error);
      toast.error(`Failed to kill process ${pid}`, {
        description: error.response?.data?.message || error.message || "An error occurred"
      });
    } finally {
      setKilling(false);
    }
  };

  const confirmKill = (pid) => {
    setSelectedPid(pid);
    setKillDialogOpen(true);
  };

  const handleViewQuery = (query) => {
    setSelectedQuery(query);
    setQueryDialogOpen(true);
    setCopied(false);
  };

  const handleCopyQuery = async () => {
    if (selectedQuery) {
      try {
        await navigator.clipboard.writeText(selectedQuery);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <span>Connections for Database:</span>
              <span className="text-primary mono-text">{database?.name}</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Active and idle connections for this database
            </DialogDescription>
          </DialogHeader>

          {/* Database Info Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
            <div>
              <p className="text-xs text-muted-foreground">Total Connections</p>
              <p className="text-lg font-bold text-foreground">{database?.total || 0}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-lg font-bold text-success">{database?.active || 0}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Idle</p>
              <p className="text-lg font-bold text-muted-foreground">{database?.idle || 0}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Memory (MB)</p>
              <p className="text-lg font-bold text-foreground">
                {database?.memory?.toFixed(2) || "0.00"}
              </p>
            </div>
          </div>

          {/* Connections Table */}
          <ScrollArea className="h-96 rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="font-semibold">PID</TableHead>
                  <TableHead className="font-semibold">User</TableHead>
                  <TableHead className="font-semibold">State</TableHead>
                  <TableHead className="font-semibold">Duration</TableHead>
                  <TableHead className="font-semibold">Query Start</TableHead>
                  <TableHead className="font-semibold">Query</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {database?.conexiones && database.conexiones.length > 0 ? (
                  database.conexiones.map((conn, index) => (
                    <TableRow key={index} className="table-row-hover">
                      <TableCell className="font-medium mono-text">{conn.pid}</TableCell>
                      <TableCell className="text-sm">{conn.user}</TableCell>
                      <TableCell>{getStateBadge(conn.state)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono">
                          {formatDuration(conn.duration)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground mono-text">
                        {formatTimestamp(conn.query_start)}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div className="flex items-center gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className="text-sm text-muted-foreground truncate mono-text cursor-help flex-1">
                                  {conn.query}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-lg">
                                <p className="text-xs mono-text whitespace-pre-wrap">{conn.query}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewQuery(conn.query)}
                                  className="h-7 w-7 p-0 flex-shrink-0"
                                >
                                  <Code2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>View full query</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => confirmKill(conn.pid)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Kill connection</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No connections available for this database
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Kill Confirmation Dialog */}
      <AlertDialog open={killDialogOpen} onOpenChange={setKillDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kill Connection?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to terminate the connection with PID <span className="font-bold mono-text">{selectedPid}</span>?
              This action cannot be undone and will immediately terminate the active query.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={killing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleKillConnection(selectedPid)}
              disabled={killing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {killing ? "Killing..." : "Kill Connection"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Query View Dialog */}
      <Dialog open={queryDialogOpen} onOpenChange={setQueryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="h-5 w-5 text-primary" />
              Query Details
            </DialogTitle>
            <DialogDescription>
              Full SQL query - Click copy button to copy to clipboard
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Query Display */}
            <div className="relative">
              <ScrollArea className="h-96 rounded-md border border-border bg-muted/30 p-4">
                <pre className="text-sm mono-text text-foreground whitespace-pre-wrap break-words">
                  {selectedQuery}
                </pre>
              </ScrollArea>
              
              {/* Copy Button */}
              <Button
                onClick={handleCopyQuery}
                size="sm"
                variant="outline"
                className="absolute top-2 right-2 gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-success" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy Query
                  </>
                )}
              </Button>
            </div>

            {/* Query Stats */}
            <div className="grid grid-cols-2 gap-4 p-3 bg-muted/30 rounded-lg border border-border">
              <div>
                <p className="text-xs text-muted-foreground">Query Length</p>
                <p className="text-sm font-medium text-foreground mono-text">
                  {selectedQuery?.length || 0} characters
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Lines</p>
                <p className="text-sm font-medium text-foreground mono-text">
                  {selectedQuery?.split('\n').length || 0} lines
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConnectionsModal;
